import axios from "axios";
import {
  GET_COMPLIANCE_RULES,
  GET_CSRF_TOKEN,
  GET_ENTITY_COMPLIANCE_COUNTDOWN,
  START_COMPLIANCE_COUNTDOWN,
} from "@/utils/constants/constants";

export default {
  methods: {
    getTokenKeys() {
      const tokenKeys = [];
      Reflect.set(tokenKeys, 0, "csrf_token");
      Reflect.set(tokenKeys, 1, "csrfToken");
      Reflect.set(tokenKeys, 2, "token");
      Reflect.set(tokenKeys, 3, "csrftoken");
      Reflect.set(tokenKeys, 4, "csrf");
      return tokenKeys;
    },

    isObjectValue(value) {
      return typeof value === "object" && value !== null;
    },

    extractTokenFromResponseData(data) {
      if (!data) return null;
      if (typeof data === "string") return data;

      for (const key of this.getTokenKeys()) {
        if (!Reflect.has(data, key)) continue;
        const tokenValue = Reflect.get(data, key);
        if (tokenValue) return tokenValue;
      }

      return this.findTokenInObject(data);
    },

    applyTokenToHeaders(token) {
      this.token = token;
      this.headers = {
        ...this.headers,
        "X-CSRFToken": token,
      };
    },

    async initializeToken() {
      const response = await axios.get(GET_CSRF_TOKEN, {
        headers: this.headers,
      });

      const responseToken = this.extractTokenFromResponseData(response.data);
      const token = responseToken || this.getTokenFromCookies();

      if (!token) {
        this.show_error("No CSRF token found in response or cookies");
        return;
      }

      this.applyTokenToHeaders(token);
    },

    getTokenFromObjectByKeys(obj) {
      for (const key of this.getTokenKeys()) {
        if (!Reflect.has(obj, key)) continue;
        const tokenValue = Reflect.get(obj, key);
        if (tokenValue) return tokenValue;
      }
      return null;
    },

    findTokenInNestedObjectValues(obj) {
      const keys = Object.keys(obj);
      for (const key of keys) {
        if (!Reflect.has(obj, key)) continue;

        const value = Reflect.get(obj, key);
        if (!this.isObjectValue(value)) continue;

        const found = this.findTokenInObject(value);
        if (found) return found;
      }
      return null;
    },

    findTokenInObject(obj) {
      if (!this.isObjectValue(obj)) return null;
      return this.getTokenFromObjectByKeys(obj) || this.findTokenInNestedObjectValues(obj);
    },

    parseDaysTextDuration(durationString) {
      const match = durationString.match(/(\d+)\s*days?/i);
      if (!match) return null;

      const days = Reflect.get(match, 1);
      return days ? parseInt(days, 10) : null;
    },

    parseDayTimeDuration(durationString) {
      const daysMatch = durationString.match(/^(\d+)\s+(\d+):(\d+):(\d+)$/);
      if (!daysMatch) return null;

      const days = Reflect.get(daysMatch, 1);
      return days ? parseInt(days, 10) : null;
    },

    parseTimeOnlyDuration(durationString) {
      const timeMatch = durationString.match(/^(\d+):(\d+):(\d+)$/);
      if (!timeMatch) return null;

      const hours = parseInt(Reflect.get(timeMatch, 1), 10);
      return Math.round(hours / 24);
    },

    getTokenFromCookies() {
      const name = "csrftoken";
      if (!document.cookie || document.cookie === "") {
        return null;
      }

      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(name + "=")) {
          return decodeURIComponent(trimmedCookie.substring(name.length + 1));
        }
      }

      return null;
    },

    async refreshToken() {
      await this.initializeToken();
      this.show_success("Token refreshed successfully");
    },

    open_conditions_page(rule) {
      sessionStorage.setItem("current_rule_id", rule.id);
      sessionStorage.setItem("current_rule_name", rule.name);
      sessionStorage.setItem("current_form_id", this.current_form_id);
      sessionStorage.setItem("current_form_name", this.current_form_name);

      this.$router.push({
        name: "ConditionManagement",
        query: {
          rule_id: rule.id,
          rule_name: rule.name,
          form_id: this.current_form_id,
          form_name: this.current_form_name,
        },
      });
    },

    daysToDuration(days) {
      if (days === null || days === undefined) return null;
      if (days === 0) return "0 days";
      return `${days} days`;
    },

    durationToDays(durationString) {
      if (!durationString) return 0;

      const parsers = [
        this.parseDaysTextDuration,
        this.parseDayTimeDuration,
        this.parseTimeOnlyDuration,
      ];

      for (const parser of parsers) {
        const parsedValue = parser.call(this, durationString);
        if (parsedValue !== null) return parsedValue;
      }

      return 0;
    },

    getFrequencyTypeFromDays(days) {
      const frequencyMap = new Map([
        [7, "weekly"],
        [14, "biweekly"],
        [30, "monthly"],
        [90, "quarterly"],
        [180, "biannually"],
        [365, "annually"],
      ]);

      return frequencyMap.get(days) || "custom";
    },

    get_compliance_rules() {
      this.loading = true;

      axios
        .get(GET_COMPLIANCE_RULES, {
          headers: this.headers,
          params: {
            entity_type_id: this.entity_type_id,
            form_id: this.form_id,
          },
        })
        .then(response => {
          this.rules = response.data?.rules || [];
          this.merge_countdown_with_rules();

          this.$nextTick(() => {
            this.initialize_data_table();
          });
        })
        .catch(error => {
          this.show_error("Error fetching compliance rules: " + error.message);
          if (error.response && error.response.status === 403) {
            this.refreshToken();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    merge_countdown_with_rules() {
      if (this.countdown_data.length > 0) {
        const merged_rules = this.rules.map(rule => {
          const countdown = this.countdown_data.find(c => c.rule_id === rule.id);
          const merged_rule = {
            ...rule,
            days_remaining: countdown?.days_remaining,
            countdown_status: countdown?.status,
            next_eval_date: countdown?.next_eval_date,
          };
          return merged_rule;
        });
        this.rules = merged_rules;
        return merged_rules;
      }
    },

    hasCountdownValue(days_remaining) {
      return days_remaining || days_remaining === 0;
    },

    isOverdueCountdown(days_remaining, countdown_status) {
      if (countdown_status === "Overdue") return true;
      return days_remaining < 0;
    },

    get_countdown_badge_class(days_remaining, countdown_status) {
      if (!this.hasCountdownValue(days_remaining)) return "badge bg-secondary";
      if (this.isOverdueCountdown(days_remaining, countdown_status)) return "badge bg-danger";
      if (days_remaining <= 7) return "badge bg-warning text-dark";
      if (days_remaining <= 14) return "badge bg-info";
      return "badge bg-success";
    },

    format_countdown_display(days_remaining, countdown_status) {
      if (!days_remaining && days_remaining !== 0) {
        return countdown_status || "Not Started";
      }
      if (days_remaining < 0) {
        return `Overdue by ${Math.abs(days_remaining)} days`;
      }
      if (days_remaining === 0) {
        return "Due Today";
      }
      return `${days_remaining} days left`;
    },

    open_assign_questions_modal(rule) {
      this.selected_rule = rule;
      this.show_assign_modal = true;
    },

    close_assign_questions_modal() {
      this.show_assign_modal = false;
      this.selected_rule = null;
    },

    async refresh_countdown_data() {
      const entity_profile_id = sessionStorage.getItem("entity_profile_id");

      if (entity_profile_id) {
        this.loading = true;
        await this.get_entity_compliance_countdown(entity_profile_id);
        this.$nextTick(() => {
          this.initialize_data_table();
        });
        this.loading = false;
        this.show_success("Countdown data refreshed");
      } else {
        this.show_info(
          "Entity profile not selected. Countdown data will be available when viewing specific entity."
        );
      }
    },

    async get_entity_compliance_countdown(entity_profile_id) {
      if (!entity_profile_id) {
        return;
      }

      return axios
        .get(GET_ENTITY_COMPLIANCE_COUNTDOWN, {
          headers: this.headers,
          params: {
            entity_profile_id,
          },
        })
        .then(response => {
          if (response.data?.status === "success") {
            this.countdown_data = response.data.compliance_countdown || [];
            this.merge_countdown_with_rules();
          }
        })
        .catch(() => {});
    },

    async start_compliance_countdown(entity_profile_id, rule_ids = null) {
      if (!entity_profile_id) {
        return;
      }

      const payload = {
        entity_profile_id,
      };

      if (rule_ids) {
        Reflect.set(
          payload,
          "compliance_rule_ids",
          Array.isArray(rule_ids) ? rule_ids : [rule_ids]
        );
      }

      const response = await axios.post(START_COMPLIANCE_COUNTDOWN, payload, {
        headers: this.headers,
      });

      if (response.data?.status === "success") {
        await this.get_entity_compliance_countdown(entity_profile_id);
      }
    },
  },
};
